import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getBalance,
  getTiers,
  getSavedCard,
  getTransactions,
  createCheckout,
  quickPurchase,
  configureAutoReload,
  addCard,
  updateCard,
  removeSavedCard
} from '../../../../apiLayer/dashBoardAPIs/paygAPIs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../ui/Card';
import { Button } from '../../../ui/Button';
import { Badge } from '../../../ui/Badge';
import { Switch } from '../../../ui/Switch';
import { Input } from '../../../ui/Input';
import { Skeleton } from '../../../ui/Skeleton';
import { cn } from '../../../../lib/utils';
import {
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  Clock,
  Plus,
  AlertCircle,
  Loader2,
  Sparkles
} from 'lucide-react';

const PAYGBilling = ({ userInfo }) => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const [transactions, setTransactions] = useState(null);

  const [balanceLoading, setBalanceLoading] = useState(true);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [autoReloadEnabled, setAutoReloadEnabled] = useState(false);
  const [autoReloadThreshold, setAutoReloadThreshold] = useState(5);
  const [autoReloadAmount, setAutoReloadAmount] = useState(25);
  const [showAutoReloadSettings, setShowAutoReloadSettings] = useState(false);

  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [toast, setToast] = useState(null);
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  const fetchBalance = useCallback(async () => {
    try {
      setBalanceLoading(true);
      const data = await getBalance();
      setBalance(data);
    } catch (err) {
      console.error('Error fetching balance:', err);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const fetchTiers = useCallback(async () => {
    try {
      setTiersLoading(true);
      const data = await getTiers();
      setTiers(data);
    } catch (err) {
      console.error('Error fetching tiers:', err);
    } finally {
      setTiersLoading(false);
    }
  }, []);

  const fetchSavedCard = useCallback(async () => {
    try {
      setCardLoading(true);
      const data = await getSavedCard();
      setSavedCard(data);
    } catch (err) {
      console.error('Error fetching saved card:', err);
    } finally {
      setCardLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      setTransactionsLoading(true);
      const data = await getTransactions(20, 0);
      setTransactions(data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchTiers();
    fetchSavedCard();
    fetchTransactions();
  }, [fetchBalance, fetchTiers, fetchSavedCard, fetchTransactions]);

  useEffect(() => {
    if (balance) {
      setAutoReloadEnabled(balance.auto_reload_enabled || false);
      setAutoReloadThreshold(balance.auto_reload_threshold || 5);
      setAutoReloadAmount(balance.auto_reload_amount || 25);
    }
  }, [balance]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleTierPurchase = async (tierAmount) => {
    setShowSwitchModal(true);
    return;
  };

  const handleCustomPurchase = async () => {
    setShowSwitchModal(true);
    return;
  };

  const handleAddCard = async () => {
    setShowSwitchModal(true);
    return;
  };

  const handleUpdateCard = async () => {
    try {
      setActionLoading(true);
      const successUrl = `${window.location.origin}/vscode/dashboard?card_updated=true`;
      const cancelUrl = `${window.location.origin}/vscode/dashboard`;
      const result = await updateCard(successUrl, cancelUrl);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to update card.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveCardClick = async () => {
    if (window.confirm('Are you sure you want to remove your saved card?')) {
      try {
        setActionLoading(true);
        await removeSavedCard();
        setSavedCard(null);
        setToast({ type: 'success', message: 'Card removed successfully.' });
      } catch (err) {
        setToast({ type: 'error', message: 'Failed to remove card.' });
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleSaveAutoReload = async () => {
    setShowSwitchModal(true);
    return;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCardBrandIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa': return 'VISA';
      case 'mastercard': return 'MC';
      case 'amex': return 'AMEX';
      default: return brand?.toUpperCase() || 'CARD';
    }
  };

  const balancePercentage = balance?.total_purchased > 0
    ? ((balance.display_balance || balance.balance) / balance.total_purchased) * 100
    : 0;

  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      {/* Toast Notification */}
      {toast && (
        <div className={cn(
          "fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg transition-all",
          toast.type === 'success' && "bg-green-500/90 text-white",
          toast.type === 'error' && "bg-red-500/90 text-white",
          toast.type === 'info' && "bg-blue-500/90 text-white"
        )}>
          {toast.type === 'success' && <CheckCircle className="w-5 h-5" />}
          {toast.type === 'error' && <XCircle className="w-5 h-5" />}
          {toast.type === 'info' && <AlertCircle className="w-5 h-5" />}
          <span className="font-medium">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 hover:opacity-70">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Sixth Credits</h1>
        <p className="text-sm text-muted-foreground">Manage your pay-as-you-go credits and billing</p>
      </div>

      {/* PAYG Retirement Banner */}
      <div className="max-w-5xl mb-6">
        <div className="relative overflow-hidden rounded-lg border border-purple-500/20 bg-gradient-to-r from-purple-500/10 via-violet-500/5 to-transparent p-5">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-white mb-1">
                Fixed Subscription Plans Are Now Available
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We're transitioning from credits to fixed plans. Once your current credits are exhausted,
                new credit purchases will no longer be available. Subscribe to a plan to continue using
                Sixth without interruption.
              </p>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-violet-500 hover:opacity-90 text-white border-0 gap-2 text-sm"
              size="sm"
            >
              View Plans <ArrowUpRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-6 max-w-5xl">
        {/* Current Balance */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-primary" />
                <CardTitle>Current Balance</CardTitle>
              </div>
              <button
                onClick={fetchBalance}
                disabled={balanceLoading}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                title="Refresh balance"
              >
                <RefreshCw className={cn("w-4 h-4 text-muted-foreground", balanceLoading && "animate-spin")} />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {balanceLoading && !balance ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-2 w-full rounded-full" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-4xl font-bold text-white">
                        {balance?.formatted_balance || `$${(balance?.display_balance || balance?.balance || 0).toFixed(2)}`}
                      </span>
                      {balanceLoading && balance && (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Available API credits</p>
                  </div>
                  <Button onClick={() => setShowSwitchModal(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add Credits
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-purple-500 transition-all duration-500"
                      style={{ width: `${Math.min(balancePercentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{balancePercentage.toFixed(0)}% remaining</span>
                    <span>Total purchased: ${balance?.total_purchased?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>

                {balance?.low_balance_warning && (
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <span className="text-sm text-yellow-200">
                      Low balance! Consider adding more credits to avoid service interruption.
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Quick Top-Up */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              <CardTitle>Quick Top-Up</CardTitle>
            </div>
            <CardDescription>
              {savedCard?.has_saved_card
                ? 'Click to instantly add credits with your saved card'
                : 'Select an amount to purchase credits'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tiersLoading && !tiers ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-20 rounded-lg" />
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  {tiers?.tiers?.map((tier) => (
                    <button
                      key={tier.amount}
                      onClick={() => handleTierPurchase(tier.amount)}
                      disabled={purchaseLoading}
                      className={cn(
                        "p-4 rounded-lg border transition-all duration-200 text-center",
                        "bg-white/5 border-white/10 hover:bg-white/10 hover:border-primary/50",
                        purchaseLoading && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <div className="text-lg font-bold text-white">${tier.amount}</div>
                    </button>
                  ))}
                </div>

                {showCustomAmount && (
                  <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          min="1"
                          max="500"
                          placeholder="Enter amount (1-500)"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          className="pl-7 bg-white/5 border-white/10"
                        />
                      </div>
                      <Button onClick={handleCustomPurchase} disabled={purchaseLoading || !customAmount}>
                        {purchaseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Purchase'}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <CardTitle>Payment Method</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {cardLoading && !savedCard ? (
              <Skeleton className="h-16 w-full" />
            ) : savedCard?.has_saved_card ? (
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                    {getCardBrandIcon(savedCard.card_brand)}
                  </div>
                  <div>
                    <div className="text-white font-medium">
                      {savedCard.card_brand?.charAt(0).toUpperCase() + savedCard.card_brand?.slice(1)} ending in {savedCard.card_last4}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Expires {savedCard.card_exp_month}/{savedCard.card_exp_year}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleUpdateCard} disabled={actionLoading}>
                    Update
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveCardClick}
                    disabled={actionLoading}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No payment method saved</p>
                <Button onClick={handleAddCard} disabled={actionLoading}>
                  Add Payment Card
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-Reload */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-primary" />
                <CardTitle>Auto-Reload</CardTitle>
              </div>
              <Switch
                checked={autoReloadEnabled}
                onCheckedChange={(checked) => {
                  setAutoReloadEnabled(checked);
                  setShowAutoReloadSettings(true);
                }}
              />
            </div>
            <CardDescription>
              Automatically add credits when your balance falls below a threshold
            </CardDescription>
          </CardHeader>
          {(showAutoReloadSettings || autoReloadEnabled) && (
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">
                      Reload when balance falls below
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={autoReloadThreshold}
                        onChange={(e) => setAutoReloadThreshold(parseFloat(e.target.value) || 5)}
                        className="pl-7 bg-white/5 border-white/10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">
                      Amount to reload
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        min="5"
                        max="500"
                        value={autoReloadAmount}
                        onChange={(e) => setAutoReloadAmount(parseFloat(e.target.value) || 25)}
                        className="pl-7 bg-white/5 border-white/10"
                      />
                    </div>
                  </div>
                </div>
                <Button onClick={handleSaveAutoReload} size="sm">
                  Save Settings
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Transaction History */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              <CardTitle>Transaction History</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {transactionsLoading && !transactions ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : transactions?.transactions?.length > 0 ? (
              <div className="space-y-2">
                {transactions.transactions.map((tx, idx) => (
                  <div key={tx.id || idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center",
                        tx.type === 'credit' || tx.type === 'purchase'
                          ? "bg-green-500/10"
                          : "bg-red-500/10"
                      )}>
                        {tx.type === 'credit' || tx.type === 'purchase' ? (
                          <ArrowDownRight className="w-4 h-4 text-green-400" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-white font-medium">
                          {tx.description || (tx.type === 'credit' ? 'Credits Added' : 'Usage')}
                        </div>
                        <div className="text-xs text-muted-foreground">{formatDate(tx.created_at || tx.timestamp)}</div>
                      </div>
                    </div>
                    <span className={cn(
                      "font-medium text-sm",
                      tx.type === 'credit' || tx.type === 'purchase' ? "text-green-400" : "text-red-400"
                    )}>
                      {tx.type === 'credit' || tx.type === 'purchase' ? '+' : '-'}${Math.abs(tx.display_amount || tx.amount || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No transactions yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Switch to Fixed Plans Modal */}
      {showSwitchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-[#121219] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-500/10">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">We're Upgrading Your Experience</h3>
                  <p className="text-sm text-muted-foreground">Better value, simpler billing</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                We're retiring the credit-based system in favor of fixed subscription plans that offer better value and a simpler experience. Your existing credits will still work until they run out, but new credit purchases are no longer available.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Switch to one of our fixed plans to get predictable billing and more features.
              </p>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSwitchModal(false)}
                >
                  Maybe Later
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-purple-500 to-violet-500 hover:opacity-90 text-white border-0 gap-2"
                  onClick={() => navigate('/pricing')}
                >
                  View Plans <ArrowUpRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PAYGBilling;
